import { Component, OnInit, Optional, ElementRef, OnDestroy } from '@angular/core';
import { PlycaDataService } from '../../../services/plyca.service';
import { generateRandomIdentifier, UIService } from '../../ui.service';
import {
  ProcurementProjectDTO, EnvelopeDTO
} from '../../api/plyca-dtos-v2';
import { Configuration } from '../../conf/app.constants';
import { TranslateService } from '@ngx-translate/core';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import { WaitLoadingService } from '../../components/common/waitLoading/wait-loading.service';
import {
  TenderV2DTO, ElectronicTenderSubmission, FileDetailsDTO, TenderingPhaseDTO,
  TenderingPhaseState, ElectronicTenderEnvelope, SubmissionFileDataDTO, ElectronicTenderStatusType, I18nData
} from '../../api/plyca-dtos';
import { PaginationDTO } from '../../api/PaginationModel';
import { forkJoin, Observable, iif, of, throwError } from 'rxjs';
import * as lodash from 'lodash';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { UploadFilesComponent } from '../../components/common/upload/upload.component';
import { HelpService, HelpType } from '../../components/common/help/help.service';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { ConfirmationService } from 'primeng-lts/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng-lts/dynamicdialog';
import { BaseComponent } from '../../components/common/baseComponent/base.component';


@Component({
  selector: 'tenderSubmissions',
  templateUrl: 'tenderSubmissions.template.html',
  providers: [DialogService]
})
export class TenderSubmissionsComponent extends BaseComponent implements OnInit, OnDestroy {

  public windowHash: any;
  tagsParent: string[];

  private project: ProcurementProjectDTO;

  public ElectronicTenderStatusType = ElectronicTenderStatusType;

  public indexes = [];
  public webProject = true;
  public loaded = false;
  private reloadTenderList = false;
  public tender: TenderV2DTO;
  public submissions: Array<ElectronicTenderSubmission>;
  public tenderingPhases: Array<TenderingPhaseDTO>;

  constructor(
    protected configuration: Configuration,
    protected plycaService: PlycaDataService,
    protected headerActionsService: HeaderActionsService,
    protected translationService: TranslateService,
    protected uiService: UIService,
    protected waitService: WaitLoadingService,
    protected helpService: HelpService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private plycaErrorHandler: PlycaErrorHandler,
    @Optional() protected dialogConfig: DynamicDialogConfig,
    @Optional() protected ref: DynamicDialogRef
  ) {
    super();
    this.helpService.getStoreTagsObservable().pipe(
      switchMap((helpTags) => this.tagsParent = helpTags)).subscribe();;
    if (dialogConfig && dialogConfig.data) {
      this.tender = dialogConfig.data.tender;
      this.project = dialogConfig.data.project;
      this.windowHash = generateRandomIdentifier();
      this.webProject = this.project.formId != undefined;
    }
  }

  ngOnInit(): void {
    this.waitService.startWaiting();
    this.subscribe(this.queryIsWebProject().pipe(
      tap((isWebProject: string) => this.webProject = isWebProject === 'true'),
      switchMap(() => forkJoin([
        this.loadTenderSubmissions().pipe(
          catchError((error: any) => {
            this.uiService.showMessage(this.translationService.instant('TenderInfo.submissions.errors.SUBMISSIONS'),
              new I18nData('General.error'),
              'errorStyle');
            return throwError(error);
          })
        ),
        iif(() => this.webProject,
          this.plycaService.query<TenderingPhaseDTO>(this.configuration.TENDERPHASE_SERVICE_URL,
            {
              'procurementProjectId': this.project.identifier,
              'expand': 'envelopes,type',
              'sort': 'initDate,asc'
            }).pipe(
              tap((phases: PaginationDTO<TenderingPhaseDTO>) => {
                if (phases && phases.content) {
                  this.tenderingPhases = phases.content.filter((phase: TenderingPhaseDTO) => phase.state !== TenderingPhaseState.CREATED);
                }
              }),
              catchError((error: any) => {
                this.uiService.showMessage(this.translationService.instant('TenderInfo.submissions.errors.PHASE'),
                  new I18nData('General.error'),
                  'errorStyle');
                return throwError(error);
              })
            ),
          of({})
        )
      ])
      )
    ),
      () => {
        this.initHeader();
        this.waitService.cancelWaiting();
        this.loaded = true;
      },
      (error: any) => {
        console.error(error);
        this.waitService.cancelWaiting();
        this.ref.close({ reloadTenderList: false });
      });
  }

  ngOnDestroy(): void {
    this.subscribe(this.helpService.showContextHelpObservable(this.tagsParent).pipe(
      finalize(() => {
        this.headerActionsService.clearWindowActions(this.windowHash);
        this.ref = null;
        this.dialogConfig = null;
        super.ngOnDestroy();
      })));
  }

  private loadTenderSubmissions(): Observable<PaginationDTO<ElectronicTenderSubmission>> {
    this.submissions = undefined;
    return this.plycaService.query<ElectronicTenderSubmission>(this.configuration.TENDER_SUBMISSIONS_SERVICE_URL, {
      'projectId': this.project.identifier,
      'tenderId': this.tender.identifier,
      'expand': 'communication,paxe.file,footPrint.file'
    }).pipe(tap((submissions: PaginationDTO<ElectronicTenderSubmission>) => {
      if (submissions) {
        let tmpSubmissions = submissions.content
          .filter((submission: ElectronicTenderSubmission) => !this.webProject
            || submission.tenderingPhase.state !== TenderingPhaseState.CREATED);
        this.submissions = tmpSubmissions;
        if (this.webProject) {
          // mostrar acordeon abierto para fases no finalizadas
          tmpSubmissions.forEach((submission: ElectronicTenderSubmission, index: number) => {
            if (submission.tenderingPhase.endDate.getTime() > new Date().getTime()
              && submission.tenderingPhase.initDate.getTime() <= new Date().getTime()) {
              this.indexes.push(index);
            }
          });
        }
      }
      if (this.indexes && this.indexes.length === 0) {
        // si estan todas las fases finalizadas dejo abierta la mas moderna
        this.indexes.push(0);
      }
    }));
  }

  private queryIsWebProject(): Observable<string> {
    return this.plycaService.simpleGet<string>(this.configuration.TENDER_SUBMISSIONS_SERVICE_URL + '/isTenderingWebProcess',
      {
        'procurementProjectId': this.project.identifier
      },
      'text');
  }

  private initHeader() {

    this.subscribe(this.helpService.showContextHelpObservable(HelpType.ADD_TENDER));
    this.headerActionsService.clearWindowActions(this.windowHash);
    this.headerActionsService.addWindowDivider(this.windowHash);
    this.subscribe(this.headerActionsService.addWindowAction('General.close', 'fas fa-lg fa-times',
      'btn-outline-secondary', true, this.windowHash),
      () => {
        this.ref.close({ reloadTenderList: this.reloadTenderList });
      });
  }


  public getDocumentClass(property: string): any {
    return {
      identifier: (this.tender.documentClass) ? this.tender.documentClass.identifier : '00006',
      configuration: {
        allowMultipleFile: false,
        allowedFileExtensions: (property) ? (('paxe' === property) ? ['paxe'] : ['pdf']) : ['paxe', 'pdf']
      }
    }
  }

  public getEnvelopes(phase: TenderingPhaseDTO): string {
    return TenderingPhaseDTO.getEnvelopesDescription(phase);
  }

  public getPhaseSubmission(phase: TenderingPhaseDTO): ElectronicTenderSubmission {
    return (this.submissions) ? this.submissions.find((submission: ElectronicTenderSubmission) => submission.tenderingPhase
      && submission.tenderingPhase.identifier === phase.identifier)
      :
      undefined;
  }

  public deletePaxe(submission: ElectronicTenderSubmission) {
    this.confirmationService.confirm({
      message: this.translationService.instant('General.confirm.action.delete'),
      accept: () => {
        let tmpSubmission = lodash.cloneDeep(submission);
        tmpSubmission.paxe.file = undefined;
        submission.status = undefined;
        this.updateTenderSubmission(tmpSubmission);
      }
    });
  }

  public addPaxe(fileItem: FileDetailsDTO, submission: ElectronicTenderSubmission, uploadComponent?: UploadFilesComponent) {
    let tmpSubmission = lodash.cloneDeep(submission);
    if (!tmpSubmission.paxe) {
      tmpSubmission.paxe = new SubmissionFileDataDTO();
    }
    tmpSubmission.paxe.file = fileItem;
    if (uploadComponent) {
      uploadComponent.uploader.clearQueue();
    }
    submission.status = undefined;
    this.updateTenderSubmission(tmpSubmission);
  }


  public deleteFootPrint(submission: ElectronicTenderSubmission) {
    this.confirmationService.confirm({
      message: this.translationService.instant('General.confirm.action.delete'),
      accept: () => {
        let tmpSubmission = lodash.cloneDeep(submission);
        tmpSubmission.footPrint.file = undefined;
        submission.status = undefined;
        this.updateTenderSubmission(tmpSubmission);
      }
    });
  }

  public addFootPrint(fileItem: FileDetailsDTO, submission: ElectronicTenderSubmission) {
    let tmpSubmission = lodash.cloneDeep(submission);
    if (!tmpSubmission.footPrint) {
      tmpSubmission.footPrint = new SubmissionFileDataDTO();
    }
    tmpSubmission.footPrint.file = fileItem;
    submission.status = undefined;
    this.updateTenderSubmission(tmpSubmission);
  }

  public createNewTenderSubmission(fileItem: FileDetailsDTO, phase: TenderingPhaseDTO) {
    let submission = new ElectronicTenderSubmission();
    submission.tender = this.tender;
    submission.tenderingPhase = phase;
    if (fileItem.name.endsWith('paxe')) {
      submission.paxe = new SubmissionFileDataDTO();
      submission.paxe.file = fileItem;
    } else {
      submission.footPrint = new SubmissionFileDataDTO();
      submission.footPrint.file = fileItem;
    }
    let submittedEnvelopes = new Array<ElectronicTenderEnvelope>();
    submission.submittedEnvelopes = submittedEnvelopes;
    if (phase && phase.envelopes) {
      phase.envelopes.forEach((envelope: EnvelopeDTO) => {
        let submittedEnv = new ElectronicTenderEnvelope();
        submittedEnv.envelope = envelope;
        submittedEnvelopes.push(submittedEnv);
      });
    }
    this.waitService.startWaiting();
    this.subscribe(this.plycaService.insert<ElectronicTenderSubmission>(this.configuration.TENDER_SUBMISSIONS_SERVICE_URL, submission)
      .pipe(
        tap(() => this.subscribe(this.loadTenderSubmissions()))
      ),
      () => this.reloadTenderList = true,
      (error: any) => this.manageError(error),
      () => {
        this.waitService.cancelWaiting();
      }
    );
  }

  public getHash(tmpSubmission: ElectronicTenderSubmission): void {
    this.subscribe(this.plycaService.get<any>(this.configuration.FILEDETAILS_SERVICE_URL + tmpSubmission.paxe.file.identifier + '/actions/hash/'
      , 'SHA-512', undefined, 'text'),
      (fileHash: any) => {
        tmpSubmission.paxe.hash = fileHash;
      });
  }

  public getStatus(submission: ElectronicTenderSubmission): void {
    if (!submission.status) {
      this.subscribe(this.plycaService.get<ElectronicTenderSubmission>(this.configuration.TENDER_SUBMISSIONS_SERVICE_URL, submission.identifier
        , { expand: 'expansionSet:detail,status' }),
        (detail: ElectronicTenderSubmission) => submission.status = detail.status
      );
    }
  }

  public getAlertClass(status: ElectronicTenderStatusType): string {
    let alertClass: string;
    switch (status) {
      case ElectronicTenderStatusType.INVALID_FOOTPRINT:
      case ElectronicTenderStatusType.INVALID_PAXE:
      case ElectronicTenderStatusType.INVALID_PAIR: {
        alertClass = 'alert-danger';
        break;
      }
      case ElectronicTenderStatusType.OK: {
        alertClass = 'alert-success';
        break;
      }
      default: {
        alertClass = 'alert-warning';
        break;
      }
    }
    return alertClass;
  }


  public getAlertIcon(status: ElectronicTenderStatusType): string {
    let alertClass: string;
    switch (status) {
      case ElectronicTenderStatusType.OK: {
        alertClass = 'fa-check';
        break;
      }
      default: {
        alertClass = 'fa-1-5x fa-exclamation-circle';
        break;
      }
    }
    return alertClass;
  }

  public getAlertMessage(status: ElectronicTenderStatusType): string {
    return this.translationService.instant('TenderInfo.submissions.errors.' + status);
  }

  private updateTenderSubmission(tmpSubmission: ElectronicTenderSubmission) {
    this.waitService.startWaiting();
    tmpSubmission.tender = this.tender;
    this.subscribe(this.plycaService.update<ElectronicTenderSubmission>(this.configuration.TENDER_SUBMISSIONS_SERVICE_URL, tmpSubmission),
      () => {
        this.reloadTenderList = true;
        this.subscribe(this.loadTenderSubmissions());
      },
      (error: any) => this.manageError(error),
      () => {
        this.waitService.cancelWaiting();
      }
    );
  }

  private manageError(error: any): void {
    this.waitService.cancelWaiting();
    this.plycaErrorHandler.showServerError(error);
  }

}